import React, { useEffect, useMemo, useState } from 'react';
import { ActiveTab, TABS, BRAND_GRADIENT } from '../types';

interface TabBarProps {
  activeTab: ActiveTab;
  onTabChange: (tab: ActiveTab) => void;
}

const ORDER_KEY = 'analytics_v2_tab_order';

/**
 * Load the user's saved order of the non-Home tabs from localStorage.
 * New tabs added to TABS after the user saved their order are appended
 * at the end so nothing ever disappears from the UI.
 */
function loadOrder(): ActiveTab[] {
  const allNonHome = TABS.filter((t) => t.key !== 'home').map((t) => t.key);
  try {
    const raw = localStorage.getItem(ORDER_KEY);
    if (!raw) return allNonHome;
    const saved: ActiveTab[] = JSON.parse(raw);
    // Keep saved entries that still exist, in saved order
    const validSaved = saved.filter((k) => allNonHome.includes(k));
    // Append any newly-added tabs the user hasn't seen yet
    const missing = allNonHome.filter((k) => !validSaved.includes(k));
    return [...validSaved, ...missing];
  } catch {
    return allNonHome;
  }
}

function saveOrder(order: ActiveTab[]): void {
  try {
    localStorage.setItem(ORDER_KEY, JSON.stringify(order));
  } catch {
    // ignore quota / private-mode errors
  }
}

const TabBar: React.FC<TabBarProps> = ({ activeTab, onTabChange }) => {
  const [order, setOrder] = useState<ActiveTab[]>(() => loadOrder());
  const [dragKey, setDragKey] = useState<ActiveTab | null>(null);
  const [overKey, setOverKey] = useState<ActiveTab | null>(null);

  // Persist whenever order changes
  useEffect(() => {
    saveOrder(order);
  }, [order]);

  // Quick lookup from key → label, so we can render in any order.
  const labelByKey = useMemo(() => {
    const m: Record<string, string> = {};
    for (const t of TABS) m[t.key] = t.label;
    return m;
  }, []);

  const handleDragStart = (key: ActiveTab) => (e: React.DragEvent) => {
    setDragKey(key);
    e.dataTransfer.effectAllowed = 'move';
    // Firefox needs something in dataTransfer to start a drag
    e.dataTransfer.setData('text/plain', key);
  };

  const handleDragOver = (key: ActiveTab) => (e: React.DragEvent) => {
    if (!dragKey || dragKey === key) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (overKey !== key) setOverKey(key);
  };

  const handleDragLeave = () => {
    setOverKey(null);
  };

  const handleDrop = (targetKey: ActiveTab) => (e: React.DragEvent) => {
    e.preventDefault();
    const sourceKey = dragKey;
    setDragKey(null);
    setOverKey(null);
    if (!sourceKey || sourceKey === targetKey) return;

    setOrder((prev) => {
      const next = prev.filter((k) => k !== sourceKey);
      const targetIdx = next.indexOf(targetKey);
      if (targetIdx === -1) return prev;
      next.splice(targetIdx, 0, sourceKey);
      return next;
    });
  };

  const handleDragEnd = () => {
    setDragKey(null);
    setOverKey(null);
  };

  const renderTab = (key: ActiveTab, draggable: boolean) => {
    const isActive = activeTab === key;
    const isDragging = dragKey === key;
    const isOver = overKey === key;
    return (
      <button
        key={key}
        onClick={() => onTabChange(key)}
        draggable={draggable}
        onDragStart={draggable ? handleDragStart(key) : undefined}
        onDragOver={draggable ? handleDragOver(key) : undefined}
        onDragLeave={draggable ? handleDragLeave : undefined}
        onDrop={draggable ? handleDrop(key) : undefined}
        onDragEnd={draggable ? handleDragEnd : undefined}
        className={`px-3 py-2 sm:py-1.5 text-xs font-semibold rounded-md transition-all duration-200 whitespace-nowrap ${
          isActive
            ? `bg-gradient-to-r ${BRAND_GRADIENT} text-white shadow`
            : 'bg-white/5 text-gray-300 hover:bg-white/10 border border-white/10'
        } ${draggable ? 'sm:cursor-grab sm:active:cursor-grabbing' : ''} ${
          isDragging ? 'opacity-40' : ''
        } ${isOver ? 'ring-2 ring-[#FF9C15]' : ''}`}
        title={draggable ? 'Drag to reorder' : undefined}
      >
        {labelByKey[key]}
      </button>
    );
  };

  return (
    <div className="flex items-center gap-1 mb-4 overflow-x-auto">
      {/* Home is pinned — never draggable, always first */}
      {renderTab('home', false)}
      {/* Thin divider so the pinned/draggable split is visually obvious */}
      <div className="w-px h-5 bg-white/10 mx-1 flex-shrink-0" />
      {order.map((key) => renderTab(key, true))}
    </div>
  );
};

export default TabBar;
